import React from "react";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useFormState } from "react-final-form";
import { AutocompleteField } from "../../AutocompleteField";
import {
  usersTypes,
  platformsAccessTypes,
  platformsTypes,
  countriesAccessTypes,
  VISIBILITY_LABEL_KEYS,
} from "@/shared/schemas";
import { countryOptions } from "@/shared/utils/countries";
import type { PlatformBlock } from "../types";
import { Paper } from "@mui/material";

interface BlockVisibilityFormProps {
  name?: string;
  block: PlatformBlock;
  platformId?: string;
}

/**
 * Компонент настроек видимости блока.
 */
export const BlockVisibilityForm: React.FC<BlockVisibilityFormProps> = ({
  name,
}) => {
  const { t } = useTranslation();
  const basePath = name
    ? `${name}.visibilityConditions`
    : "visibilityConditions";

  // Подписи опций приходят из shared-словаря русскими; в партнёрском кабинете
  // они переводятся по ключам `visibility.*`, в админке остаётся русский
  // defaultValue.
  const visibilityOptions = (
    source: Record<string, string>,
    keyPrefix: string
  ) =>
    Object.entries(source).map(([value, label]) => ({
      value,
      label: t(`${keyPrefix}.${value}`, { defaultValue: label }),
    }));

  const { values } = useFormState({
    subscription: { values: true },
  });
  const visibilityConditions = (values as any)?.visibilityConditions;
  const countriesType = visibilityConditions?.countries?.type;
  const platformsType = visibilityConditions?.applicationPlatforms?.type;

  return (
    <Paper sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography variant="subtitle1" color="text.secondary">
            {t("platformBlocks.visibility.countriesTitle")}
          </Typography>
        </Grid>
        <Grid size={12}>
          <AutocompleteField
            name={`${basePath}.countries.type`}
            label={t("platformBlocks.visibility.typeLabel")}
            fullWidth
            required
            options={visibilityOptions(
              countriesAccessTypes,
              VISIBILITY_LABEL_KEYS.countriesAccessTypes
            )}
          />
        </Grid>
        {countriesType && countriesType !== "ALL" && (
          <Grid size={12}>
            <AutocompleteField
              name={`${basePath}.countries.countryCodes`}
              label={t("platformBlocks.visibility.countriesLabel")}
              fullWidth
              multiple
              options={countryOptions}
            />
          </Grid>
        )}

        {/* Видимость на платформах */}
        <Grid size={12}>
          <Typography variant="subtitle1" color="text.secondary">
            {t("platformBlocks.visibility.platformsTitle")}
          </Typography>
        </Grid>
        <Grid size={12}>
          <AutocompleteField
            name={`${basePath}.applicationPlatforms.type`}
            label={t("platformBlocks.visibility.typeLabel")}
            fullWidth
            required
            options={visibilityOptions(
              platformsAccessTypes,
              VISIBILITY_LABEL_KEYS.platformsAccessTypes
            )}
          />
        </Grid>
        {platformsType && platformsType !== "ALL" && (
          <Grid size={12}>
            <AutocompleteField
              name={`${basePath}.applicationPlatforms.platforms`}
              label={t("platformBlocks.visibility.platformsLabel")}
              fullWidth
              multiple
              options={Object.entries(platformsTypes).map(([value, label]) => ({
                label,
                value,
              }))}
            />
          </Grid>
        )}

        {/* Видимость пользователям */}
        <Grid size={12}>
          <Typography variant="subtitle1" color="text.secondary">
            {t("platformBlocks.visibility.usersTitle")}
          </Typography>
        </Grid>
        <Grid size={12}>
          <AutocompleteField
            name={`${basePath}.userType`}
            label={t("platformBlocks.visibility.typeLabel")}
            fullWidth
            required
            options={visibilityOptions(
              usersTypes,
              VISIBILITY_LABEL_KEYS.usersTypes
            )}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};
